import classnames from 'classnames/bind'
import { default as NextLink } from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { GLOBAL_BREAKPOINTS } from '~/@types/breakpoints'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'

import Slider, { useSliderState } from '@unlikelystudio/react-slider'

import { ArrowLeftIcon } from '~/components/Abstracts/Icons'

import { useFilter } from '~/providers/FilterProvider'
import { usePagination } from '~/providers/PaginationProvider'
import { useStyle } from '~/providers/StyleProvider'

import useBreakpoint from '~/hooks/useBreakpoint'
import useLocale from '~/hooks/useLocale'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export type PaginationProps = {
  className?: string
  maxPage?: number
}

const ITEMS_LENGTH_DESKTOP = 15
const ITEMS_LENGTH_MOBILE = 10

function Pagination({ className, maxPage }: PaginationProps) {
  const router = useRouter()
  const locale = useLocale()
  const { currentPage, getRouterParams } = usePagination()
  const [{ setSlideIndex, slideIndex, nextSlide, prevSlide }, setSliderState] =
    useSliderState()
  const [initialIndex, setInitialIndex] = useState<number>()
  const [itemsVisible, setItemsVisible] = useState(ITEMS_LENGTH_DESKTOP)
  const { userFiltering } = useFilter()
  const customSliderRef = useRef()
  const slidedIndex = useRef<number>(initialIndex)
  const isMobile = useBreakpoint(GLOBAL_BREAKPOINTS.MD)

  const maxIndex = maxPage - itemsVisible

  const linkStyle = useStyle({
    color: GlobalThemeColors.Black,
    textPreset: GlobalTextPreset.Cta12GroteskRegular,
    textStyling: GlobalTextStyling.UpperCase,
  })

  useEffect(() => {
    isMobile
      ? setItemsVisible(ITEMS_LENGTH_MOBILE)
      : setItemsVisible(ITEMS_LENGTH_DESKTOP)
  }, [isMobile])

  useEffect(() => {
    if (currentPage < 2) setInitialIndex(0)
    else if (currentPage > maxIndex) setInitialIndex(maxIndex)
    else setInitialIndex(currentPage - 3)
  }, [currentPage, maxIndex])

  useEffect(() => {
    if (initialIndex !== slideIndex) {
      setSlideIndex(initialIndex)
    }
  }, [initialIndex])

  const renderLink = (value: number) => {
    // Get router params and pass page value
    const params = getRouterParams(value)

    return (
      Boolean(value) && (
        <NextLink
          prefetch={false}
          // We are using an href so this is not a shallow push
          onClick={(e) => {
            e.preventDefault()
            router.push(params, undefined, { shallow: true, locale })
          }}
          href={params}
          shallow={userFiltering}
          locale={locale}
          key={value}>
          <a
            className={cx(
              css.link,
              currentPage === value && css.active,
              linkStyle,
            )}>
            {value}
          </a>
        </NextLink>
      )
    )
  }
  const renderArrow = (orientation: 'left' | 'right') => (
    <div
      className={cx(css.arrow, css[orientation])}
      onClick={() => (orientation === 'left' ? prevSlide() : nextSlide())}>
      <ArrowLeftIcon className={css.icon} />
    </div>
  )

  return (
    <div className={className}>
      <div className={css.sliderContainer} style={{ width: 24 * itemsVisible }}>
        {slideIndex > 0 && renderArrow('left')}
        <Slider
          className={cx(css.slider)}
          setSliderState={setSliderState}
          initialSlideIndex={initialIndex}
          snap
          autoBlockSlideIndexChange={true}
          ref={customSliderRef}
          onSlideIndexChange={(index) => (slidedIndex.current = index)}>
          {[...Array(maxPage + 1)]?.map((_, index) => renderLink(index))}
        </Slider>
        {slideIndex < maxIndex && renderArrow('right')}
      </div>
    </div>
  )
}

Pagination.defaultProps = {}

export default Pagination
